import React, { useState, useEffect, createContext, useContext } from 'react';
import Navbar from './components/Navbar';
import Dashboard from './components/Dashboard';
import Neighborhoods from './components/Neighborhoods';
import Schools from './components/Schools';
import Checklist from './components/Checklist';
import Investments from './components/Investments';
import CommunityFAQ from './components/CommunityFAQ';
import MapExplorer from './components/MapExplorer';
import Quiz from './components/Quiz';
import GuidesHub from './components/GuidesHub';
import NRIGuide from './components/NRIGuide';
import RNORGuide from './components/RNORGuide';
import SchoolGuide from './components/SchoolGuide';
import { ErrorBoundary } from './components/ErrorBoundary';
import { AppView, UserProfile, AppData, DEFAULT_USER_PROFILE } from './types';
import { appStorage } from './utils/storage';
import { safeJsonParse, validateUserProfile } from './utils/validation';
import { trackPageView, trackEvent } from './utils/analytics';
import { AlertTriangle, X, Loader2, RefreshCw } from 'lucide-react';

// Data Context
const DataContext = createContext<AppData | null>(null);

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error("useData must be used within a DataProvider");
  }
  return context;
};

const App: React.FC = () => {
  const [currentView, setCurrentView] = useState<AppView>(AppView.DASHBOARD);
  const [userProfile, setUserProfile] = useState<UserProfile>(DEFAULT_USER_PROFILE);
  const [showStorageWarning, setShowStorageWarning] = useState(false);
  
  // Data Loading State
  const [data, setData] = useState<AppData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Check storage availability
    if (!appStorage.isAvailable) {
      setShowStorageWarning(true);
    }

    // Load saved profile if available, otherwise keep defaults
    const savedProfile = appStorage.getItem('puneRelocationProfile');
    const parsedProfile = safeJsonParse<Record<string, unknown>>(savedProfile);
    const validatedProfile = validateUserProfile(parsedProfile);

    if (validatedProfile) {
      setUserProfile({ ...validatedProfile, isPersonalized: true });
    } else if (savedProfile) {
      console.warn("Invalid profile data detected, clearing localStorage");
      appStorage.removeItem('puneRelocationProfile');
    }

    // Fetch JSON Data
    const fetchData = async () => {
      try {
        setIsLoading(true);
        // Ensure paths match your public/data structure
        const [neighborhoodsRes, schoolsRes, checklistRes, investmentsRes, faqsRes] = await Promise.all([
          fetch('/data/neighborhoods.json'),
          fetch('/data/schools.json'),
          fetch('/data/relocation_checklist.json'),
          fetch('/data/investments.json'),
          fetch('/data/faqs.json')
        ]);

        if (!neighborhoodsRes.ok || !schoolsRes.ok || !checklistRes.ok || !investmentsRes.ok || !faqsRes.ok) {
          throw new Error("Failed to load one or more data files");
        }

        const appData: AppData = {
          neighborhoods: await neighborhoodsRes.json(),
          schools: await schoolsRes.json(),
          checklist: await checklistRes.json(),
          investments: await investmentsRes.json(),
          faqs: await faqsRes.json()
        };

        setData(appData);
        setError(null);
      } catch (err: any) {
        console.error("Data Load Error:", err);
        setError("We're having trouble loading the relocation data. Please check your connection.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  // Track virtual pageviews for SPA navigation
  useEffect(() => {
    trackPageView(currentView);
  }, [currentView]);

  const handleProfileComplete = (profile: UserProfile) => {
    const personalizedProfile = { ...profile, isPersonalized: true };
    setUserProfile(personalizedProfile);
    appStorage.setItem('puneRelocationProfile', JSON.stringify(personalizedProfile));
    trackEvent('quiz_completed', { country: profile.sourceCountry });
    setCurrentView(AppView.DASHBOARD);
  };

  const handleResetProfile = () => {
    if (window.confirm("Are you sure you want to reset your preferences? You'll see default recommendations.")) {
      appStorage.clear();
      try {
        window.localStorage.clear();
        window.sessionStorage.clear();
      } catch (e) {
        console.error("Native storage clear failed", e);
      }
      setUserProfile(DEFAULT_USER_PROFILE);
      setCurrentView(AppView.DASHBOARD);
    }
  };

  const handleEditProfile = () => {
    setCurrentView(AppView.QUIZ);
  };

  const renderView = () => {
    switch (currentView) {
      case AppView.QUIZ:
        return <Quiz onComplete={handleProfileComplete} initialData={userProfile} />;
      case AppView.LANDING:
      case AppView.DASHBOARD:
        return (
          <Dashboard
            userProfile={userProfile}
            setView={setCurrentView}
            onEditProfile={handleEditProfile}
            onResetProfile={handleResetProfile}
          />
        );
      case AppView.MAP:
        return <MapExplorer setView={setCurrentView} />;
      case AppView.NEIGHBORHOODS:
        return <Neighborhoods userProfile={userProfile} />;
      case AppView.SCHOOLS:
        return <Schools userProfile={userProfile} />;
      case AppView.CHECKLIST:
        return <Checklist userProfile={userProfile} />;
      case AppView.INVESTMENTS:
        return <Investments userProfile={userProfile} />;
      case AppView.COMMUNITY:
        return <CommunityFAQ setView={setCurrentView} />;
      case AppView.GUIDES:
        return <GuidesHub setView={setCurrentView} />;
      case AppView.NRI_GUIDE:
        return <NRIGuide setView={setCurrentView} />;
      case AppView.RNOR_GUIDE:
        return <RNORGuide setView={setCurrentView} />;
      case AppView.SCHOOL_GUIDE:
        return <SchoolGuide setView={setCurrentView} />;
      default:
        return (
          <div className="flex flex-col items-center justify-center h-full p-8 text-center">
            <h2 className="text-2xl font-bold text-slate-800 mb-2">Page Not Found</h2>
            <p className="text-slate-500 mb-6">The section you are looking for does not exist.</p>
            <button
              onClick={() => setCurrentView(AppView.DASHBOARD)}
              className="bg-indigo-600 text-white px-6 py-2 rounded-lg font-bold hover:bg-indigo-700 transition"
            >
              Go to Home
            </button>
          </div>
        );
    }
  };

  // Loading Screen
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Loader2 size={48} className="animate-spin text-indigo-600 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-800">Loading Resources...</h2>
          <p className="text-slate-500">Preparing your relocation guide</p>
        </div>
      </div>
    );
  }

  // Error Screen
  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md text-center border border-slate-200">
          <div className="bg-red-50 p-4 rounded-full inline-block mb-4">
            <AlertTriangle size={48} className="text-red-500" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-2">Data Loading Failed</h2>
          <p className="text-slate-500 mb-6">{error || "Unknown error occurred"}</p>
          <button 
            onClick={() => window.location.reload()}
            className="bg-indigo-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-indigo-700 transition flex items-center justify-center gap-2 mx-auto"
          >
            <RefreshCw size={20} /> Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <DataContext.Provider value={data}>
        <div className="flex flex-col md:flex-row min-h-screen bg-slate-50 text-slate-900 font-sans">
          <Navbar currentView={currentView} setView={setCurrentView} />
          
          <main className={`flex-1 w-full md:h-screen md:overflow-y-auto custom-scrollbar relative`}>
             {showStorageWarning && (
               <div className="bg-amber-100 border-b border-amber-200 text-amber-900 px-4 py-2 text-xs font-bold flex justify-between items-center sticky top-0 z-50">
                 <div className="flex items-center gap-2">
                   <AlertTriangle size={14} className="text-amber-600" />
                   <span>Private Browsing detected. Your progress will not be saved after you close this tab.</span>
                 </div>
                 <button onClick={() => setShowStorageWarning(false)} className="hover:bg-amber-200 p-1 rounded">
                   <X size={14} />
                 </button>
               </div>
             )}
             
             {renderView()}
          </main>
        </div>
      </DataContext.Provider>
    </ErrorBoundary>
  );
};

export default App;